import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Seo } from "../components/Seo";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Lock, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error(t("reset.missingToken"));
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < 8) {
      toast.error(t("reset.tooShort"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("reset.mismatch"));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success(t("reset.success"));
      // Backend response includes account_type in unified endpoint (fallback: restaurant portal).
      const target = res?.data?.account_type === "customer" ? "/login" : "/restaurant";
      setTimeout(() => navigate(target), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail || t("reset.error");
      toast.error(typeof detail === "string" ? detail : t("reset.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Nouveau mot de passe — Vytali"
        description="Choisissez un nouveau mot de passe pour votre compte Vytali."
        path="/reset-password"
        noIndex
      />
      <Header />
      <div className="max-w-md mx-auto px-6 py-12">
        <button
          data-testid="reset-back"
          onClick={() => navigate("/restaurant")}
          className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#E63946] mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> {t("forgot.back")}
        </button>

        <div className="bg-white border-2 border-[#2B2B2B] rounded-2xl p-6 md:p-8 shadow-[8px_8px_0px_#E63946]">
          {done ? (
            <div data-testid="reset-done" className="text-center py-4">
              <CheckCircle2 className="w-14 h-14 text-[#2A9D8F] mx-auto" />
              <h1 className="mt-4 text-2xl font-display font-black tracking-tight text-[#2B2B2B]">
                {t("reset.doneTitle")}
              </h1>
              <p className="mt-3 text-sm text-[#5C5C5C]">{t("reset.doneDesc")}</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl md:text-3xl font-display font-black tracking-tight text-[#2B2B2B]">
                {t("reset.title")}
              </h1>
              <p className="mt-2 text-sm text-[#5C5C5C]">{t("reset.subtitle")}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5C5C]" />
                  <input
                    data-testid="reset-new-password"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("reset.newPassword")}
                    className="w-full h-12 pl-10 pr-4 rounded-xl bg-[#FDFBF7] border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5C5C]" />
                  <input
                    data-testid="reset-confirm-password"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={t("reset.confirmPassword")}
                    className="w-full h-12 pl-10 pr-4 rounded-xl bg-[#FDFBF7] border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors"
                  />
                </div>
                <button
                  data-testid="reset-submit"
                  type="submit"
                  disabled={busy || !token}
                  className="w-full h-12 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.02] transition-transform disabled:opacity-50"
                >
                  {busy ? "…" : t("reset.submit")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
